<script setup lang="ts">
/**
 * The cookie banner: a steel plate along the foot of the screen until the
 * visitor chooses. "Accept all" and "Reject non-essential" are the same plate,
 * size and colour (no nudging); "Choose" opens the per-category dialog. It does
 * not block the page behind it. Owns the plate only; the choice is
 * `consent/consent.ts`, the dialog is `CookieSettings`.
 */
import { useI18n } from '@/i18n';
import { consent } from '@/consent/consent';
import GameIcon from '../kit/GameIcon.vue';
import ActionButton from '../panel/ActionButton.vue';

defineEmits<{ policy: [] }>();
const { t } = useI18n();
</script>

<template>
  <section class="banner k-panel" role="region" :aria-label="t('consent.title')" data-test="cookie-banner">
    <div class="says">
      <GameIcon class="icon" name="orders" :size="44" />
      <div class="copy">
        <h2 class="k-title title">{{ t('consent.title') }}</h2>
        <p class="body">
          {{ t('consent.body') }}
          <button type="button" class="link" data-test="cookie-policy-link" @click="$emit('policy')">{{ t('consent.policy') }}</button>
        </p>
      </div>
    </div>
    <div class="answers">
      <ActionButton variant="primary" size="sm" data-test="consent-accept" @click="consent.acceptAll()">{{ t('consent.acceptAll') }}</ActionButton>
      <ActionButton variant="primary" size="sm" data-test="consent-reject" @click="consent.rejectAll()">{{ t('consent.rejectAll') }}</ActionButton>
      <ActionButton size="sm" data-test="consent-customise" @click="consent.openSettings()">{{ t('consent.customise') }}</ActionButton>
    </div>
  </section>
</template>

<style scoped>
/* Above the station windows (20), below dialogs (40): the policy and the settings open over it. */
.banner {
  position: fixed;
  z-index: 35;
  right: max(8px, env(safe-area-inset-right));
  bottom: max(8px, env(safe-area-inset-bottom));
  left: max(8px, env(safe-area-inset-left));
  display: grid;
  gap: 8px;
  max-width: 56rem;
  margin: 0 auto;
  padding: clamp(14px, 2vw, 22px);
}

.says {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.icon {
  flex: none;
}

.copy {
  min-width: 0;
}

.title {
  margin: 0;
  font-size: 1.1rem;
}

.body {
  margin: 2px 0 0;
  font-family: var(--font-game-body);
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  color: #dde6ee;
}

.link {
  min-height: 44px;
  padding: 0 2px;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 700;
  color: var(--kit-gold);
  text-decoration: underline;
  cursor: pointer;
  touch-action: manipulation;
}

.answers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

/* "Choose" takes the full row on a phone so the two answers stay equal halves. */
.answers > :last-child {
  grid-column: 1 / -1;
}

@media (min-width: 640px) {
  .answers {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .answers > :last-child {
    grid-column: auto;
    order: -1;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .banner {
    animation: banner-rise 300ms ease-out;
  }
}

@keyframes banner-rise {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
}
</style>
